import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControl,
  Icon,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { subDays, startOfDay, endOfDay } from 'date-fns';

import axiosInstance from 'utils/axiosUtil';
import { withRouter } from 'utils';
import { TitleTooltip, DynamicTable } from 'components';
import { StoreUtil } from 'doctivity-shared/utils';
import { listStarkLogs, getStarkProviderTotals } from 'store/actions/starkActions';
import { listActiveUsers } from 'store/actions/usersActions';
import { getCmsAnnualLimit } from './starkConstants';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLOR = { ok: 'success', warning: 'warning', exceeded: 'error' };

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].filter((y) => y >= 2026);
const minimumStartDate = new Date(2018, 0, 1);

// ── sessionStorage helpers (shared with MileageTrackingPage) ──────────────────
const isValidDate = (d) => d instanceof Date && !isNaN(d);

function readSessionDates() {
  const startRaw = sessionStorage.getItem('startDate');
  const endRaw   = sessionStorage.getItem('endDate');
  const start    = startRaw ? new Date(startRaw) : null;
  const end      = endRaw   ? new Date(endRaw)   : null;
  return {
    startDate: startOfDay(isValidDate(start) ? start : subDays(new Date(), 30)),
    endDate:   endOfDay(isValidDate(end) ? end : new Date()),
  };
}

function readSessionUser() {
  const raw = sessionStorage.getItem('filterUser');
  try {
    return raw ? JSON.parse(raw) : 'All Users';
  } catch {
    return 'All Users';
  }
}

function formatDateOnly(dateStr) {
  if (!dateStr) return '—';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length !== 3) return dateStr;
  const [yr, mo, dy] = parts;
  return `${mo}/${dy}/${yr}`;
}

function formatAmount(val) {
  const n = parseFloat(val);
  return isNaN(n) ? '$0.00' : `$${n.toFixed(2)}`;
}

// Client-side sort for the individual entries table
function sortLogs(logs, order) {
  if (!order || !order.length) return logs;
  const [field, dir] = [
    order[0].slice(0, -1).join('.'),
    (order[0][order[0].length - 1] || 'DESC').toLowerCase(),
  ];
  return [...logs].sort((a, b) => {
    let av, bv;
    switch (field) {
      case 'user_name':
        av = (a.user_name || '').toLowerCase();
        bv = (b.user_name || '').toLowerCase();
        break;
      case 'total_amount':
        av = parseFloat(a.total_amount) || 0;
        bv = parseFloat(b.total_amount) || 0;
        break;
      case 'category_label':
        av = (a.category_label || '').toLowerCase();
        bv = (b.category_label || '').toLowerCase();
        break;
      default: // spending_date — DATEONLY strings sort lexicographically
        av = (a.spending_date || '') + '|' + (a.created_at || '');
        bv = (b.spending_date || '') + '|' + (b.created_at || '');
    }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  content: {
    padding: theme.spacing(3),
  },
  // Filter bar — above both cards, matches MileageTrackingPage
  filters: {
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filterSpace: {
    width: 4,
  },
  userFilter: {
    minWidth: 270,
  },
  rangeControl: {
    minWidth: 90,
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  spacer: { flex: 1 },
  // Provider summary table
  providerTableHead: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    '& th': { fontSize: 12, fontWeight: 500, padding: '6px 8px', verticalAlign: 'top' },
  },
  providerTableBody: {
    '& td': { fontSize: 13, padding: '4px 8px' },
  },
  providerRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    width: 120,
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
  },
  emptyMsg: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  affiliatedIcon: {
    fontSize: 14,
    verticalAlign: 'text-top',
    color: theme.palette.secondary.main,
  },
});

// ProviderEntryDetail removed — entries are now rendered as raw aligned TableRows

// ── Main page ──────────────────────────────────────────────────────────────────

class StarkTrackingPage extends Component {
  constructor(props) {
    super(props);
    const { startDate, endDate } = readSessionDates();

    // Default non-admins to their own entries (same as MileageTrackingPage)
    let filterUser = readSessionUser();
    if (!sessionStorage.getItem('filterUser') && !props.isAdmin && props.currentUser) {
      filterUser = props.currentUser;
    }

    this.state = {
      // Shared filters (same sessionStorage keys as MileageTrackingPage)
      startDate,
      endDate,
      filterUser,

      // Table 1 — individual entries
      query: { limit: 25, offset: 0, order: [['spending_date', 'DESC']] },

      // Table 2 — provider summary controls (year-based for annual compliance)
      year:          currentYear,
      providerQuery: { limit: 25, offset: 0, order: [['last_entry_date', 'DESC']] },

    };
  }

  componentDidMount() {
    const { dispatch, clientId } = this.props;
    if (clientId) dispatch(listActiveUsers(clientId));
    this._fetchAll();
  }

  componentDidUpdate(prevProps) {
    const { dispatch, clientId, starkInvalidatedAt } = this.props;
    if (clientId && clientId !== prevProps.clientId) {
      dispatch(listActiveUsers(clientId));
      this._fetchAll();
    }
    // Re-fetch when a note containing stark entries was just saved from anywhere
    // on this page (e.g. ProviderCommentsDialog dispatches invalidateStark).
    if (prevProps.starkInvalidatedAt !== starkInvalidatedAt) {
      this._fetchAll();
    }
  }

  _filterUserId = () => {
    const { filterUser } = this.state;
    return (filterUser && filterUser !== 'All Users') ? filterUser.id : null;
  };

  _fetchAll() {
    const { dispatch, clientId } = this.props;
    const { year, startDate, endDate } = this.state;
    if (!clientId) return;

    const filterUserId = this._filterUserId();

    // Table 1: date-range + optional single-user filter
    dispatch(listStarkLogs(clientId, {
      startDate:     startDate.toISOString(),
      endDate:       endDate.toISOString(),
      ...(filterUserId != null ? { filterUserId } : {}),
    }));

    // Table 2: year-based (annual compliance) — shows all users
    dispatch(getStarkProviderTotals(clientId, year));
  }

  // ── CSV export ──────────────────────────────────────────────────────────────

  exportCsv = () => {
    const { clientId }              = this.props;
    const { year, startDate, endDate } = this.state;
    const filterUserId = this._filterUserId();
    const params = new URLSearchParams({ clientId, year });
    params.set('startDate', startDate.toISOString());
    params.set('endDate',   endDate.toISOString());
    if (filterUserId != null) params.set('filterUserId', filterUserId);

    axiosInstance
      .get(`/v2/stark/export?${params.toString()}`, { responseType: 'blob' })
      .then((res) => {
        const url  = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href  = url;
        link.setAttribute('download', `StarkLaw_${year}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error('Stark CSV export failed', err));
  };

  exportProviderCsv = () => {
    const { year } = this.state;
    const rows     = this._getFilteredProviders();

    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const headers = ['Last Name', 'First Name', 'NPI', 'Most Recent', 'YTD Total', 'Progress %'];
    const lines   = [
      headers.join(','),
      ...rows.map((r) => [
        escape(r.last_name),
        escape(r.first_name),
        escape(r.npi),
        escape(formatDateOnly(r.last_entry_date)),
        escape(formatAmount(r.ytd_total)),
        escape(Math.round(r.pct_used || 0) + '%'),
      ].join(',')),
    ];

    const csv  = lines.join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href  = url;
    link.setAttribute('download', `StarkLaw_ProviderSummary_${year}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  _fetchProviderTotals() {
    const { dispatch, clientId } = this.props;
    const { year } = this.state;
    if (!clientId) return;
    dispatch(getStarkProviderTotals(clientId, year));
  }

  // ── Filter change handlers (same pattern as MileageTrackingPage) ─────────────

  onUserChange = (_event, newUser) => {
    sessionStorage.setItem('filterUser', JSON.stringify(newUser));
    this.setState(
      (prev) => ({ filterUser: newUser, query: { ...prev.query, offset: 0 } }),
      this._fetchAll.bind(this),
    );
  };

  onStartDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = startOfDay(newDate);
    sessionStorage.setItem('startDate', d.toISOString());
    this.setState(
      (prev) => ({ startDate: d, query: { ...prev.query, offset: 0 } }),
      this._fetchAll.bind(this),
    );
  };

  onEndDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = endOfDay(newDate);
    sessionStorage.setItem('endDate', d.toISOString());
    this.setState(
      (prev) => ({ endDate: d, query: { ...prev.query, offset: 0 } }),
      this._fetchAll.bind(this),
    );
  };

  // ── Derive filtered + sorted provider rows ──────────────────────────────────

  _getFilteredProviders() {
    const { providerTotals } = this.props;
    const { providerQuery }  = this.state;
    let rows = (providerTotals?.data || []).slice();

    // Apply DynamicTable column filters client-side
    const where = providerQuery?.where || {};
    Object.entries(where).forEach(([key, condition]) => {
      if (!condition) return;
      let searchVal = '';
      if (condition.$match)      searchVal = String(condition.$match).toLowerCase();
      else if (condition.$like)  searchVal = String(condition.$like).replace(/%/g, '').toLowerCase();
      else if (typeof condition === 'string') searchVal = condition.toLowerCase();
      if (!searchVal) return;

      rows = rows.filter((r) => {
        const val = key === 'ytd_total'
          ? formatAmount(r[key])
          : String(r[key] == null ? '' : r[key]).toLowerCase();
        return val.includes(searchVal);
      });
    });

    // Apply DynamicTable sort client-side
    const order = providerQuery?.order;
    if (order && order.length) {
      const [col, dir] = Array.isArray(order[0]) ? order[0] : [order[0], 'ASC'];
      const asc = String(dir).toUpperCase() !== 'DESC';
      rows.sort((a, b) => {
        let av = a[col], bv = b[col];
        if (col === 'ytd_total') {
          av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
        } else if (col === 'last_entry_date') {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        } else {
          av = String(av || '').toLowerCase(); bv = String(bv || '').toLowerCase();
        }
        if (av < bv) return asc ? -1 : 1;
        if (av > bv) return asc ?  1 : -1;
        return 0;
      });
    }

    return rows;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    const {
      classes, router, clientId,
      starkLogs, starkLogsLoading,
      providerTotals, providerTotalsLoading,
      users,
    } = this.props;
    const {
      year,
      startDate, endDate, filterUser,
      query,
    } = this.state;

    const showingAll  = !filterUser || filterUser === 'All Users';
    const userOptions = ['All Users', ...(StoreUtil.isLoaded(users) ? StoreUtil.getData(users) : [])];

    const logs        = starkLogs || [];
    const annualLimit = providerTotals?.data?.[0]?.annual_limit ?? getCmsAnnualLimit(year);

    // Table 1 data
    const sortedLogs  = sortLogs(logs, query.order);
    const pagedLogs   = sortedLogs.slice(query.offset, query.offset + query.limit);
    const tableData   = starkLogsLoading
      ? { placeholder: true, showLoading: true }
      : { count: logs.length, rows: pagedLogs };

    const columns = [
      ...(showingAll ? [{
        key:           'user_name',
        label:         'User',
        style:         { width: 110 },
        hideForMobile: true,
        format:        (_v, row) => row.user_name || '—',
      }] : []),
      {
        key:    'spending_date',
        label:  'Date',
        style:  { width: 90 },
        format: (v) => formatDateOnly(v),
      },
      {
        key:      'category_label',
        label:    'Category',
        style:    { width: 120 },
        sortable: false,
        format:   (v) => v || '—',
      },
      {
        key:           'description',
        label:         'Description',
        hideForMobile: true,
        sortable:      false,
        style:         { minWidth: 200 },
        format:        (v) => v || '—',
      },
      {
        key:    'total_amount',
        label:  'Amount',
        style:  { width: 90 },
        format: (v) => formatAmount(v),
      },
      {
        key:           'allocations',
        label:         'Providers',
        sortable:      false,
        hideForMobile: true,
        style:         { width: 180 },
        format:        (_v, row) => {
          const allocs = row.allocations || [];
          if (!allocs.length) return '—';
          const names = [];
          let counter = 0;
          allocs.every((a) => {
            counter++;
            if (counter > 5) {
              names.push(`(+${allocs.length - 5} more)`);
              return false;
            }
            if (a.provider) {
              const affiliatedMark = a.provider.is_affiliated ? ' *' : '';
              names.push(`${a.provider.last_name}, ${a.provider.first_name}${affiliatedMark}`);
            }
            return true;
          });
          return (
            <div style={{ whiteSpace: 'pre', paddingTop: 5, paddingBottom: 5 }}>
              {names.join('\n')}
            </div>
          );
        },
      },
    ];

    // Table 2 data — apply client-side pagination after filter+sort
    const filteredProviders = this._getFilteredProviders();
    const { providerQuery } = this.state;
    const providerOffset = providerQuery?.offset || 0;
    const providerLimit  = providerQuery?.limit  || 25;

    // Provider summary columns — affiliated icon + split name fields + NPI + totals
    const providerColumns = [
      {
        key:        'affiliated',
        label:      '',
        style:      { width: 24, minWidth: 24, textAlign: 'left' },
        sortable:   false,
        showFilter: false,
        format:     (v) => v > 0
          ? (
            <Tooltip title='Affiliated'>
              <Icon className={classes.affiliatedIcon}>hub</Icon>
            </Tooltip>
          ) : <></>,
      },
      {
        key:      'last_name',
        label:    'Last Name',
        style:    { minWidth: 120 },
        fulltext: true,
        format:   (v) => v || '—',
      },
      {
        key:      'first_name',
        label:    'First Name',
        style:    { minWidth: 120 },
        fulltext: true,
        format:   (v) => v || '—',
      },
      {
        key:    'npi',
        label:  'NPI',
        style:  { width: 110, minWidth: 110, maxWidth: 110 },
        format: (v) => v || '—',
      },
      {
        key:        'last_entry_date',
        label:      'Most Recent',
        style:      { minWidth: 130 },
        showFilter: false,
        format:     (v) => formatDateOnly(v),
      },
      {
        key:    'ytd_total',
        label:  'YTD Total',
        style:  { width: 110, minWidth: 110, maxWidth: 110 },
        format: (v) => formatAmount(v),
      },
      {
        key:        'pct_used',
        label:      'Progress',
        style:      { minWidth: 180 },
        sortable:   false,
        showFilter: false,
        format:     (_v, row) => {
          const actual = Math.round(row.pct_used || 0);
          const barPct = Math.min(actual, 100);
          const color  = STATUS_COLOR[row.status] || 'primary';
          return (
            <Box display='flex' alignItems='center' gap={1}>
              <LinearProgress
                variant='determinate'
                value={barPct}
                color={color}
                className={classes.progressBar}
              />
              <Typography variant='caption'>{actual}%</Typography>
            </Box>
          );
        },
      },
    ];

    const providerTableData = providerTotalsLoading
      ? { placeholder: true, showLoading: true }
      : {
          rows:  filteredProviders.slice(providerOffset, providerOffset + providerLimit),
          count: filteredProviders.length,
        };

    return (
      <>
        <title>Stark Law Tracking</title>

        <div className={classes.content}>

          {/* ── Filter bar — above both cards, matches MileageTrackingPage ─── */}
          <div className={classes.filters}>
            <FormControl className={classes.userFilter}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(u) =>
                  u === 'All Users' ? 'All Users' : `${u.last_name}, ${u.first_name}`
                }
                isOptionEqualToValue={(option, value) =>
                  option === value ||
                  (option !== 'All Users' && value !== 'All Users' && option.id === value.id)
                }
                autoComplete
                disableClearable
                value={filterUser}
                onChange={this.onUserChange}
                renderInput={(params) => <TextField {...params} label='User' />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='From'
                value={startDate}
                minDate={minimumStartDate}
                maxDate={endDate || new Date()}
                onChange={this.onStartDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='To'
                value={endDate}
                minDate={startDate || minimumStartDate}
                maxDate={new Date()}
                onChange={this.onEndDateChange}
                renderInput={(params) => (
                  <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />
                )}
              />
            </FormControl>
          </div>

          {/* ══════════════════════════════════════════════════════════ */}
          {/* TABLE 1 — Individual Stark Entries                        */}
          {/* ══════════════════════════════════════════════════════════ */}

          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <TitleTooltip
                  title='Non-Monetary Compensation'
                  onExport={this.exportCsv}
                />
                <div className={classes.spacer} />
                <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap', fontStyle: 'italic', paddingRight: 2 }}>
                  Add entries via Activity Notes
                </Typography>
              </div>

              <DynamicTable
                columns={columns}
                data={tableData}
                query={query}
                onQueryChange={(q) => this.setState({ query: q })}
                onRowClick={(row) => router.navigate(`/notes/stark/${row.id}`)}
                showFilters={false}
                noDataMessage='No non-monetary compensation entries for the selected date range.'
                ContainerType={Fragment}
              />
            </CardContent>
          </Card>

          {/* ══════════════════════════════════════════════════════════ */}
          {/* TABLE 2 — Provider Summary                                */}
          {/* ══════════════════════════════════════════════════════════ */}

          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <TitleTooltip title='STARK Provider Summary' onExport={this.exportProviderCsv} />
                <div className={classes.spacer} />
                <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                  {year} limit: <strong>{formatAmount(annualLimit)}</strong>
                </Typography>
                <Select
                  value={year}
                  onChange={(e) =>
                    this.setState(
                      { year: Number(e.target.value) },
                      this._fetchProviderTotals.bind(this),
                    )
                  }
                  sx={{ minWidth: 90 }}
                >
                  {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </div>

              <DynamicTable
                columns={providerColumns}
                data={providerTableData}
                query={providerQuery}
                onQueryChange={(q) => this.setState({ providerQuery: q })}
                idKey='provider_id'
                showFilters
                onRowClick={(row) => router.navigate(`/providers/${row.provider_id}/stark`)}
                noDataMessage={`No non-monetary compensation logged for ${year}.`}
                ContainerType={Fragment}
              />
            </CardContent>
          </Card>
        </div>

      </>
    );
  }
}

StarkTrackingPage.propTypes = {
  classes:              PropTypes.object.isRequired,
  dispatch:             PropTypes.func.isRequired,
  router:               PropTypes.object.isRequired,
  clientId:             PropTypes.number,
  currentUser:          PropTypes.object,
  isAdmin:              PropTypes.bool,
  starkLogs:            PropTypes.array,
  starkLogsLoading:     PropTypes.bool,
  providerTotals:       PropTypes.object,
  providerTotalsLoading: PropTypes.bool,
  users:                PropTypes.object,
  starkInvalidatedAt:   PropTypes.number,
};

function mapStateToProps(state) {
  const u = state.user || null;
  return {
    clientId:              state.app?.selectedClient || null,
    currentUser:           u ? { id: u.id, first_name: u.first_name, last_name: u.last_name } : null,
    isAdmin:               u?.is_admin || false,
    starkLogs:             state.stark?.logs?.data || [],
    starkLogsLoading:      state.stark?.logs?.loading || false,
    providerTotals:        state.stark?.providerTotals || null,
    providerTotalsLoading: state.stark?.providerTotals?.loading || false,
    users:                 state.users?.list,
    starkInvalidatedAt:    state.stark?.invalidatedAt || null,
  };
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(StarkTrackingPage)));
